import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import "./ValueSelect.css";

/** Render the open list in the app: Android's native select dialog ignores option CSS. */
export function ValueSelect({ label, value, options, onChange }: {
  label: string; value: string; options: { value: string; label: string }[]; onChange: (value: string) => void;
}) {
  const id = useId(), trigger = useRef<HTMLButtonElement>(null), list = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false), [active, setActive] = useState(0), [position, setPosition] = useState<CSSProperties>({});
  const selected = Math.max(0, options.findIndex(option => option.value === value));
  function show() { setActive(selected); setOpen(true); }
  function choose(index: number) { onChange(options[index].value); setOpen(false); trigger.current?.focus(); }
  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    function place() {
      const field = trigger.current!.getBoundingClientRect(), height = Math.min(options.length * 44 + 8, 280);
      const below = window.innerHeight - field.bottom - 8, above = field.top - 8;
      const useAbove = below < height && above > below, available = Math.max(44, useAbove ? above : below);
      setPosition({ left: Math.max(8, field.left), top: useAbove ? field.top - Math.min(height, available) - 4 : field.bottom + 4,
        width: Math.min(field.width, window.innerWidth - 16), maxHeight: available, fontSize: getComputedStyle(trigger.current!).fontSize });
    }
    place(); window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open, options.length]);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => { if (!trigger.current?.contains(event.target as Node) && !list.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);
  useEffect(() => { if (open) list.current?.children[active]?.scrollIntoView({ block: "nearest" }); }, [active, open]);
  function keyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); event.nativeEvent.stopImmediatePropagation(); setOpen(false); }
    else if (event.key === "Tab") setOpen(false);
    else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault(); if (!open) show();
      else setActive(index => event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : Math.max(0, Math.min(options.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
    } else if (open && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); choose(active); }
  }
  return <>
    <button ref={trigger} type="button" className="input value-select-trigger" role="combobox" aria-label={label}
      aria-expanded={open} aria-haspopup="listbox" aria-controls={open ? id : undefined} aria-activedescendant={open ? `${id}-${active}` : undefined}
      onKeyDown={keyDown} onBlur={() => setOpen(false)} onClick={() => open ? setOpen(false) : show()}>
      <span>{options[selected]?.label}</span><span className="value-select-chevron" aria-hidden="true" />
    </button>
    {open && createPortal(<div ref={list} id={id} role="listbox" aria-label={label} className="value-select-list" style={position}>
      {options.map((option, index) => <button type="button" role="option" id={`${id}-${index}`} key={option.value} tabIndex={-1}
        aria-selected={option.value === value} className={index === active ? "is-active" : ""}
        onPointerDown={event => event.preventDefault()} onClick={() => choose(index)}>
        <span>{option.label}</span><span aria-hidden="true">{option.value === value ? "✓" : ""}</span>
      </button>)}
    </div>, document.body)}
  </>;
}
